import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import CustomToolbar from "./UsersCustomToolbar";
import {
    getUsers,
    resetUserPassword,
    disableUserAccount,
    updateUserRole,
    createUserAccount,
    updateShouldReceiveEmails,
} from "../../api/requests";
import { roles } from "../../config/index";
import Loader from "../Loader";
import CreateUserDialog from "./CreateUserDialog";
import { useSelector } from "react-redux";

const UsersPanel = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);

    const token = useSelector((state) => state.auth.token);

    useEffect(() => {
        async function fetchData() {
            let tempData = await getUsers(token);
            setData(
                tempData.map((user, index) => {
                    return { ...user, id: index + 1 };
                })
            );
            setIsLoading(false);
        }

        if (token !== "" && open === false) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, open]);

    const handleReset = async (uid) => {
        const res = await resetUserPassword(uid);
        if (res) {
            alert("email for password reset was sent");
        }
    };
    const handleDisable = async (uid, toStatus) => {
        const ans = await disableUserAccount(uid, toStatus);
        if (ans) {
            setData((prevState) => {
                return prevState.map((item) =>
                    item.uid === uid ? { ...item, disabled: toStatus } : item
                );
            });
            alert("account state changed");
        }
    };
    const handleChangeRole = async (uid, toRole) => {
        const ans = await updateUserRole(uid, toRole);
        if (ans) {
            setData((prevState) => {
                return prevState.map((item) =>
                    item.uid === uid
                        ? { ...item, isAdmin: roles.ADMIN === toRole }
                        : item
                );
            });
            alert("user's role changed");
        }
    };
    const handleChangeShouldReceive = async (uid, toRecieve) => {
        const ans = await updateShouldReceiveEmails(uid, toRecieve);
        if (ans) {
            setData((prevState) => {
                return prevState.map((item) =>
                    item.uid === uid
                        ? { ...item, shouldReceive: 1 === toRecieve }
                        : item
                );
            });
            alert("user's to receive email changed");
        }
    };
    const handleCreateUserAccount = async (event) => {
        const data = new FormData(event.currentTarget);
        const res = await createUserAccount(
            token,
            data.get("email"),
            data.get("password")
        );
        if (res) {
            alert("User account was created successfully");
        } else {
            alert("Failed creating new user account");
        }
    };
    const columns = [
        { field: "id", headerName: "ID", width: 90 },
        {
            field: "firstName",
            headerName: "First name",
            width: 150,
        },
        {
            field: "lastName",
            headerName: "Last name",
            width: 150,
        },
        {
            field: "email",
            headerName: "Email",
            width: 200,
        },

        {
            field: "isAdmin",
            headerName: "Is Admin",
            width: 160,
            type: "boolean",
        },
        {
            field: "empty-placeholder",
            headerName: "Reset Password",
            width: 150,
            description: "This column has a render cell and is not sortable.",
            sortable: false,
            renderCell: (params) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        style={{ marginLeft: 16 }}
                        onClick={() => handleReset(params.row.uid)}
                        data-testid={"reset-btn"}
                    >
                        reset
                    </Button>
                </div>
            ),
        },
        {
            field: "disabled",
            headerName: "Disable Account",
            width: 150,
            description: "This column has a render cell and is not sortable.",
            sortable: false,
            renderCell: (params) => (
                <div>
                    {params.row.disabled ? (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() => handleDisable(params.row.uid, false)}
                            data-testid={"enable-btn"}
                        >
                            enable
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() => handleDisable(params.row.uid, true)}
                            data-testid={"disable-btn"}
                        >
                            disable
                        </Button>
                    )}
                </div>
            ),
        },
        {
            field: "change role",
            headerName: "Change Admin Role",
            width: 160,
            description: "This column has a render cell and is not sortable.",
            sortable: false,
            renderCell: (params) => (
                <div>
                    {!params.row.isAdmin ? (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() =>
                                handleChangeRole(params.row.uid, roles.ADMIN)
                            }
                            data-testid={"promote-btn"}
                        >
                            promote
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() =>
                                handleChangeRole(params.row.uid, roles.BASIC)
                            }
                            data-testid={"demote-btn"}
                        >
                            demote
                        </Button>
                    )}
                </div>
            ),
        },
        {
            field: "should receive email",
            headerName: "Should receive email",
            width: 160,
            description: "This column has a render cell and is not sortable.",
            sortable: false,
            renderCell: (params) => (
                <div>
                    {!params?.row?.shouldReceive ? (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() =>
                                handleChangeShouldReceive(params.row.uid, 1)
                            }
                            data-testid={"receive-btn"}
                        >
                            send
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            style={{ marginLeft: 16 }}
                            onClick={() =>
                                handleChangeShouldReceive(params.row.uid, 0)
                            }
                            data-testid={"not-receive-btn"}
                        >
                            do not send
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return isLoading ? (
        <Loader />
    ) : (
        <>
            <div style={{ height: "320px", width: "100%" }}>
                <DataGrid
                    rows={data}
                    columns={columns}
                    pageSize={3}
                    rowsPerPageOptions={[3]}
                    disableSelectionOnClick
                    components={{
                        Toolbar: () => <CustomToolbar setOpen={setOpen} />,
                    }}
                    disableVirtualization
                />
            </div>
            <CreateUserDialog
                open={open}
                setOpen={setOpen}
                handleCreateUser={handleCreateUserAccount}
            />
        </>
    );
};

export default UsersPanel;
